import { Component, OnInit, Input, OnChanges, SimpleChanges, NgZone, OnDestroy } from '@angular/core';
import { GoogleChartService } from '../services/google-chart.service';
import { Router, ActivatedRoute, RouterEvent, NavigationEnd } from '@angular/router';
import { rtoLoadsTbl } from '../Models/LoadsData';
import { DatePipe } from '@angular/common';
import { MinMaxDate } from '../Models/MiscModels';
import { RtoHrlyDemandService } from '../services/rto-hrly-demand.service';
import { SignalrISOdataService } from '../services/signalr-ISOdata.service';
import { MiscService } from '../services/misc.service';
import { NONE_TYPE } from '@angular/compiler/src/output/output_ast';
import { ResizedEvent } from 'angular-resize-event';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-rto-load-chart',
  templateUrl: './rto-load-chart.component.html',
  styleUrls: ['./rto-load-chart.component.css']
})


export class RtoLoadChartComponent implements OnInit, OnDestroy {
  public destroyed = new Subject<any>();
  private gLib: any;
  private minMaxDate: MinMaxDate;

  private rtoLoadDashboard: any;

  private rtoLoadLine: any;
  private rtoLoadDateSlider: any;
  public btnLabel = 'Show as Table';
  private rtoLoadTable;
  private chartTitle: string;

  constructor(private gChartService: GoogleChartService, private route: ActivatedRoute,
    private signalrService: SignalrISOdataService, private _ngZone: NgZone, private miscSvc: MiscService) {

  }

  private getOptions(chartType) {
    let options;

    switch (chartType) {

      case 'ComboChart':
        options = {
          enableInteractivity: true,
          interpolateNulls: 'false',
          series: {
            0: { type: 'bars', color: '#c8393c', targetAxisIndex: 0 },
            1: { type: 'line', targetAxisIndex: 0, pointSize: 0, color: '#13f9f6', lineWidth: 7, interpolateNulls: 'false' },
            2: {
              type: 'steppedArea', targetAxisIndex: 0,
              color: '#c0c0c0', lineWidth: 2, interpolateNulls: 'true', opacity: 0.01
            },
            3: { type: 'steppedArea', targetAxisIndex: 0, color: 'red', lineWidth: 7, interpolateNulls: 'false', opacity: 0.05 }

          },

          colors: ['#13f9f6'],
          legend: {
            position: 'top', alignment: 'end', textStyle: { fontSize: 14 },
          },
          titleTextStyle: {
            color: 'black',    // any HTML string color ('red', '#cc00cc')
            fontName: 'Montesserat', // i.e. 'Times New Roman'
            fontSize: '22', // 12, 18 whatever you want (don't specify px)
            bold: false,    // true or false
            italic: true,   // true of false
          },
          chartArea: { width: '80%', height: '80%' },
          crosshair: { trigger: 'both' },
          curveType: 'none',

          vAxis:
          {
            title: 'PJM  Load', textStyle: { fontSize: '12' }, format: '#,### MW',
            minValue: 3000, maxValue: 11000, gridlines: { count: 0 }
          },
          //   { title: 'PJM Zone Load', textStyle: { fontSize: '12' }, format: '#,### MW' }
          //   // , minValue: 2000, maxValue: 10000, gridlines: { count: 8 }

          hAxis: {
            textStyle: {
              fontSize: '12'
            },
            gridlines: {
              count: -1,
              units: {
                days: { format: ['MMM dd'] },
                hours: { format: ['HH:mm', 'ha'] },
              }
            },
            minorGridlines: {
              units: {
                hours: { format: ['hh:mm:ss a', 'ha'] },
                minutes: { format: ['HH:mm a Z', ':mm'] }
              }
            },

          },
        };

        break;

      case 'Table':
        options = {
          showRowNumber: true,
          width: '80%',
          height: '80%'
        };
        break;

      default:
        options = {};
    }

    return options;
  }

  getFilterOptions() {
    const controlOptions = {
      // Filter by the date axis.
      'filterColumnIndex': 0,
      ui: {
        'chartOptions': {
          colors: ['grey'], pointSize: 0, lineWidth: 1,
          chartArea: { width: '80%', height: '100%' },
          hAxis: {
            textStyle: {
              fontSize: '14'
            },
            gridlines: {
              count: -1,
              units: {
                days: { format: ['MMM dd'] },
                hours: { format: ['HH:mm', 'ha'] },
              }
            },
            minorGridlines: {
              units: {
                hours: { format: ['hh:mm:ss a', 'ha'] },
                minutes: { format: ['HH:mm a Z', ':mm'] }
              }
            }
          }
        },
      }
    };
    return controlOptions;
  }

  ngOnInit() {
    this.gLib = this.gChartService.getGoogle();
    this.gLib.charts.load('current', { 'packages': ['corechart', 'table', 'controls'] });
    this.gLib.charts.setOnLoadCallback(this.drawRtoLoadChart.bind(this));
    this.rtoLoadTable = new this.gLib.visualization.DataTable();

    this.signalrService.RtoLoadsRefreshMessageReceived
      .pipe(takeUntil(this.destroyed))
      .subscribe((data) => {
        this._ngZone.run(() => {
          this.updateChartDataTable(data);

        });
      });
  }

  formatAxes() {
    const date_formatter = new this.gLib.visualization.DateFormat({ pattern: 'MMM dd, yyyy,  h:mm aa' });
    date_formatter.format(this.rtoLoadTable, 0);  // Where 0 is the index of the column

    const y1formatter = new this.gLib.visualization.NumberFormat({ suffix: ' MW', pattern: '#,###' });
    y1formatter.format(this.rtoLoadTable, 1); // Apply formatter to first column

    y1formatter.format(this.rtoLoadTable, 2); // Apply formatter to second column

    // y2formatter.format(this.rtoLoadTable, 3);  Apply formatter to third column
  }

  private createChartDataTable(data: Array<rtoLoadsTbl>) {

    this.rtoLoadTable.addColumn('date', 'Date');
    // this.rtoLoadTable.addColumn('number', 'HVAC Load Sink');
    this.rtoLoadTable.addColumn('number', 'PJM RTO Load');

    this.rtoLoadTable.addColumn('number', 'PJM RTO Short Term Forecast');

    this.rtoLoadTable.addColumn({ type: 'string', role: 'style' });

    this.rtoLoadTable.addColumn('number', 'Daily Peak');
    this.rtoLoadTable.addColumn({ type: 'string', role: 'tooltip' });

   
    const evalDateTime = new Date(data[data.length - 1].evaluatedAt);
    const evalTimeStr = evalDateTime.toLocaleString() + ' (EST)';
    
    this.chartTitle = 'PJM RTO Load at 5 minute intervals. Last Reading at ' + evalTimeStr;

    this.addRowsChartDataTable(data);
  }

  private addRowsChartDataTable(data: Array<rtoLoadsTbl>) {
    const chartRow = new Array<Date | number | string | boolean>();

    for (let i = 0; i < data.length; i++) {

      chartRow.length = 0;

      const date = new Date(data[i].timestamp);
      chartRow.push(date);

      // chartRow.push(data[i].consumptionLoad / 1000);
      chartRow.push(data[i].rtoLoad);

      chartRow.push(data[i].rtoVeryShortForecast);

      chartRow.push('opacity:.5');

      const peak = data[i].peak;
      if ((peak !== 0 || peak != null) && (peak === 1)) {
        chartRow.push(160000);
        chartRow.push('Local Peak:  Timestamp ending at ' + date.toString());
      } else {
        chartRow.push(0);
        chartRow.push('Local Peak End:  Timestamp starting at ' + date.toString());
      }


      this.rtoLoadTable.addRow(chartRow);

    }
    this.formatAxes();
  }
  private updateChartDataTable(data: Array<rtoLoadsTbl>) {

    if (this.rtoLoadDashboard !== undefined) {
      const maxEvalAtStr = this.miscSvc.removeRowsInstLoad(this.rtoLoadTable, data);
      this.addRowsChartDataTable(data);
      this.updateRtoLoadChart(data, maxEvalAtStr);
    }
  }

  private createLoadChartWrapper() {   // Line chart visualization

    this.rtoLoadLine = new this.gLib.visualization.ChartWrapper({
      'chartType': 'ComboChart',
      'containerId': 'rtoLoadLine_div',
    });
    const options = this.getOptions('ComboChart');
    this.rtoLoadLine.setOptions(options);
    this.rtoLoadLine.setOption('title', this.chartTitle);
  }
  private drawRtoLoadChart(options) {

    this.createChartDataTable(this.route.snapshot.data.RtoLoadData);


    // Create a dashboard.
    const dash_container = document.getElementById('rtoLoadDashboard_div');


    this.rtoLoadDashboard = new this.gLib.visualization.Dashboard(dash_container);

    const dtNumRows = this.rtoLoadTable.getNumberOfRows();

    const MaxDt = new Date(this.rtoLoadTable.getValue(dtNumRows - 1, 0));
    const MinDt = new Date(this.rtoLoadTable.getValue(0, 0));


    // Create a date range slider
    this.rtoLoadDateSlider = new this.gLib.visualization.ControlWrapper({
      'controlType': 'ChartRangeFilter',
      'containerId': 'rtoLoadcontrol_div',

    });
    const state_obj = { 'range': { 'start': MinDt, 'end': MaxDt } };
    this.rtoLoadDateSlider.setState(state_obj);
    const controlOptions = this.getFilterOptions();
    this.rtoLoadDateSlider.setOptions(controlOptions);


    this.createLoadChartWrapper();


    // Bind rtoLoadLine to the dashboard, and to the controls
    // this will make sure our line chart is updated when our date changes
    this.rtoLoadDashboard.bind(this.rtoLoadDateSlider, this.rtoLoadLine);

    const chartType = this.rtoLoadLine.getChartType();
    if (chartType === 'Table') {
      const view = new this.gLib.visualization.DataView(this.rtoLoadTable);
      // view.hideColumns([4, 5]);
      this.rtoLoadDashboard.draw(view, { sortColumn: 0 });
    } else {
      this.rtoLoadDashboard.draw(this.rtoLoadTable);
    }
  }

  private updateRtoLoadChart(data: Array<rtoLoadsTbl>, maxEvalTimeStr: String) {

    if (this.rtoLoadDashboard !== undefined) {

      this.chartTitle = 'PJM RTO Load at 5 minute intervals. Last Reading at ' + maxEvalTimeStr;

      this.rtoLoadLine.setOption('title', this.chartTitle);

      let state_obj = this.rtoLoadDateSlider.getState();
      const startDt: Date = new Date(state_obj.range.start);
      const endDt: Date = new Date(data[data.length - 1].timestamp);

       state_obj = { 'range': { 'start': startDt, 'end': endDt } };
        this.rtoLoadDateSlider.setState(state_obj);
      const chartType = this.rtoLoadLine.getChartType();
      if (chartType === 'Table') {
        const view = new this.gLib.visualization.DataView(this.rtoLoadTable);
        // view.hideColumns([4, 5]);
        this.rtoLoadDashboard.draw(view, { sortColumn: 0 });
      } else {
        this.rtoLoadDashboard.draw(this.rtoLoadTable);
      }

    }
  }

  onResized(event: ResizedEvent) {
    const width = event.newWidth;
    const height = event.newHeight;

    if (this.rtoLoadLine !== undefined) {
      this.rtoLoadLine.draw();
      this.rtoLoadDateSlider.draw();
    }
  }
  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
  private switchChart() {

    let chartType;
    const view = new this.gLib.visualization.DataView(this.rtoLoadTable);

    view.hideColumns([4, 5]);
    // tslint:disable-next-line:triple-equals
    if (this.btnLabel == 'Show as Chart') {
      chartType = 'ComboChart';
      this.rtoLoadLine.setChartType(chartType);
      this.btnLabel = 'Show as Table';
    } else {
      chartType = 'Table';
      this.rtoLoadLine.setChartType(chartType);
      this.btnLabel = 'Show as Chart';

    }
    const ChartType = this.rtoLoadLine.getChartType();

    const options = this.getOptions(chartType);
    this.rtoLoadLine.setOptions(options);
    this.rtoLoadLine.setOption('title', this.chartTitle);
    if (chartType === 'Table') {
      this.rtoLoadDashboard.draw(view, { sortColumn: 0 });
    } else {
      this.rtoLoadDashboard.draw(this.rtoLoadTable);
    }
  }

}


